import type { AvailablePaymentMethod } from '../shared/paymentMethodChanger/PaymentMethodChanger';

/**
 * Transitional FE sort: AB-gated re-sort of payment-method tiles for the
 * `recommended-first` treatment of `order-present-payment-methods`. Control /
 * unassigned users see backend order untouched. Retire this module once the
 * treatment sort moves server-side at experiment graduation.
 */

/** Backend experiment Name (kebab) — must equal the registry key and the seed `AbExperiment.Name`. */
export const ORDERED_METHODS_EXPERIMENT = 'order-present-payment-methods';
/** Treatment variant code — must equal the seeded `AbExperimentVariant.ExperimentCode`. */
export const RECOMMENDED_FIRST = 'recommended-first';

/**
 * AB-gated ordering of payment-method tiles. Non-treatment: returns input
 * unchanged (caller has already pre-sorted availability-last). `recommended-first`:
 * sorts unavailable-last (e.g. Apple Pay off non-Apple), then methods with a
 * numeric `order` before those without, ascending by `order`, stable for ties.
 * Pure; same ref for control, new array for treatment.
 */
export function orderPaymentMethods(
  methods: AvailablePaymentMethod[],
  variant: string
): AvailablePaymentMethod[] {
  if (variant !== RECOMMENDED_FIRST) {
    return methods;
  }

  return methods
    .map((m, index) => ({ m, index }))
    .sort((a, b) => {
      const aAvail = a.m.isAvailable ?? true;
      const bAvail = b.m.isAvailable ?? true;
      if (aAvail !== bAvail) {
        return aAvail ? -1 : 1;
      }

      const aHas = typeof a.m.order === 'number';
      const bHas = typeof b.m.order === 'number';
      if (aHas !== bHas) {
        return aHas ? -1 : 1;
      }

      if (aHas && bHas && a.m.order !== b.m.order) {
        return (a.m.order as number) - (b.m.order as number);
      }

      return a.index - b.index;
    })
    .map((x) => x.m);
}

/**
 * Auto-preselect for the treatment variant: the order===1 method, but only if
 * available on the device. Returns undefined for control, no order===1, or when
 * it is unavailable — auto-navigating there would dead-end the user on its
 * input form (the changer's onSelect guard does not run for auto-preselect).
 * Backend uniqueness keeps order===1 singular; defensively picks the first
 * available match.
 */
export function pickPreselection(
  methods: AvailablePaymentMethod[],
  variant: string
): AvailablePaymentMethod | undefined {
  if (variant !== RECOMMENDED_FIRST) {
    return undefined;
  }
  return methods.find((m) => m.order === 1 && (m.isAvailable ?? true));
}
